import { useState, useEffect, useCallback } from 'react';
import { FileText, Download, RefreshCw } from 'lucide-react';
import {
  fetchTaxForms,
  fetchTaxFormPdfUrl,
  type TaxForm1099K,
  type TaxFormStatus,
} from '../api/tax-reporting.api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function statusBadgeClasses(status: TaxFormStatus): string {
  switch (status) {
    case 'CREATED':          return 'bg-info/15 text-info border border-info/25';
    case 'FINALIZED':        return 'bg-warning/15 text-warning border border-warning/25';
    case 'FILED':            return 'bg-success/15 text-success border border-success/25';
    case 'RECIPIENT_NOTIFIED': return 'bg-success/15 text-success border border-success/25';
    default:                 return 'bg-muted text-muted-foreground border border-border';
  }
}

function statusLabel(status: TaxFormStatus): string {
  switch (status) {
    case 'CREATED':            return 'Created';
    case 'FINALIZED':          return 'Finalized';
    case 'FILED':              return 'Filed';
    case 'RECIPIENT_NOTIFIED': return 'Notified';
    default:                   return status;
  }
}

export function TaxReportsPage(): JSX.Element {
  const [forms, setForms] = useState<TaxForm1099K[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchTaxForms();
      setForms(data.forms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tax forms');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownloadPdf = async (id: string) => {
    setDownloadingId(id);
    setDownloadError(null);
    try {
      const { url } = await fetchTaxFormPdfUrl(id);
      window.open(url, '_blank');
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Failed to get download link');
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)] pt-5" data-testid="tax-reports-page">
        <div className="space-y-3">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="tax-reports-page">
      {/* Toolbar */}
      <div className="px-[var(--page-padding-x)] pt-5 pb-4 flex justify-end">
        <Button variant="outline" onClick={loadData} className="min-h-[44px] gap-2">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Refresh
        </Button>
      </div>

      <div className="flex flex-col gap-4 px-[var(--page-padding-x)]">
        {/* Error banner */}
        {error && (
          <div
            className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
            role="alert"
          >
            <p className="text-destructive m-0">{error}</p>
            <Button variant="outline" size="sm" onClick={loadData} className="mt-2">
              <RefreshCw className="h-3 w-3 mr-1" /> Retry
            </Button>
          </div>
        )}

        {/* Download error */}
        {downloadError && (
          <div
            className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
            role="alert"
          >
            <p className="text-destructive m-0">{downloadError}</p>
          </div>
        )}

        {/* Forms list */}
        {forms.length === 0 ? (
          <div
            className="rounded-lg border border-border bg-card p-8 text-center"
            data-testid="tax-forms-empty-state"
          >
            <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm font-medium text-foreground mb-1">No forms yet</p>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Stripe will issue a 1099-K automatically once your annual payment volume
              exceeds $20,000 and 200 transactions.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="w-full text-sm" data-testid="tax-forms-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 py-3">Tax Year</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 py-3">Gross Amount</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 py-3">Transactions</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 py-3">Status</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 py-3">Filed Date</th>
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map((form) => (
                  <tr
                    key={form.id}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                    data-testid={`tax-form-row-${form.id}`}
                  >
                    <td className="px-4 py-3 font-medium text-foreground">{form.taxYear}</td>
                    <td className="px-4 py-3 text-foreground">{formatCents(form.grossAmount)}</td>
                    <td className="px-4 py-3 text-foreground">{form.transactionCount.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClasses(form.status)}`}>
                        {statusLabel(form.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(form.filedAt)}</td>
                    <td className="px-4 py-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPdf(form.id)}
                        disabled={downloadingId === form.id}
                        data-testid={`download-pdf-${form.id}`}
                        className="gap-1.5"
                      >
                        <Download className="h-3 w-3" aria-hidden="true" />
                        {downloadingId === form.id ? 'Loading...' : 'Download PDF'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default TaxReportsPage;
